/**
 * Debug Export Service
 *
 * Handles exporting debug capture data to various formats.
 */

import type { BondSeries, Candidate, Configuration, RevenueMap } from '~/types';
import type { DebugExportJSON, DebugSessionCapture } from '../types';

/**
 * Raw cashflow export format - includes everything needed for LLM analysis
 */
export interface CashflowExportData {
  $schema: 'bond-calculator-cashflows-v1';
  exportedAt: string;
  candidateId: string;
  candidateName: string;
  configurationId: string;

  // Raw data for comparison
  cashFlows: Candidate['cashFlows'];
  revenueMap: RevenueMap;
  bondSeries: BondSeries[];

  // Summary info
  totalPayments: number;
  interestOnlyPayments: number;
  dateRange: {
    first: string;
    last: string;
  };
}

/**
 * Debug Export Service
 */
export class DebugExportService {
  /**
   * Export capture data to JSON string
   */
  static exportToJSON(capture: DebugSessionCapture, pretty = true): string {
    const exportData: DebugExportJSON = {
      $schema: 'bond-calculator-debug-v1',
      exportedAt: new Date().toISOString(),
      schemaVersion: capture.schemaVersion,

      metadata: {
        candidateId: capture.candidateId,
        candidateName: capture.candidateName,
        configurationId: capture.configurationId,
        millageShape: capture.millageShapeName ?? '',
        processingTimeMs: capture.processingTimeMs,
      },

      summary: capture.summary,

      payments: capture.paymentSnapshots.map((snapshot) => ({
        paymentDate: snapshot.paymentDate,
        taxYear: snapshot.taxYear,
        paymentIndex: snapshot.paymentIndex,
        isInterestOnly: snapshot.isInterestOnly,
        fields: Object.fromEntries(
          Object.entries(snapshot.fields).map(([key, field]) => [
            key,
            {
              value: field.value,
              formatted: field.formatted,
              formula: field.formula,
            },
          ])
        ),
        bondBreakdown: snapshot.bondBreakdown?.map((bond) => ({
          bondSeriesId: bond.bondSeriesId,
          bondSeriesName: bond.bondSeriesName,
          fields: Object.fromEntries(
            Object.entries(bond.fields).map(([key, field]) => [
              key,
              {
                value: field.value,
                formatted: field.formatted,
              },
            ])
          ),
        })),
      })),
    };

    return pretty ? JSON.stringify(exportData, null, 2) : JSON.stringify(exportData);
  }

  /**
   * Download capture data as JSON file
   */
  static downloadAsJSON(capture: DebugSessionCapture): void {
    const json = DebugExportService.exportToJSON(capture);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-${capture.candidateName}-${capture.sessionId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  /**
   * Export capture data to CSV string
   */
  static exportToCSV(capture: DebugSessionCapture): string {
    // Get all unique field IDs from all snapshots
    const fieldIds = new Set<string>();
    for (const snapshot of capture.paymentSnapshots) {
      for (const id of Object.keys(snapshot.fields)) {
        fieldIds.add(id);
      }
    }
    const sortedFieldIds = Array.from(fieldIds).sort();

    // Build header row
    const headers = ['Payment Date', 'Tax Year', 'Index', 'Interest Only', ...sortedFieldIds];

    // Build data rows
    const rows = capture.paymentSnapshots.map((snapshot) => {
      const row = [
        snapshot.paymentDate,
        String(snapshot.taxYear),
        String(snapshot.paymentIndex),
        snapshot.isInterestOnly ? 'Yes' : 'No',
        ...sortedFieldIds.map((id) => {
          const field = snapshot.fields[id];
          return field ? String(field.value ?? '') : '';
        }),
      ];
      return row;
    });

    // Combine and format as CSV
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            const escaped = String(cell).replace(/"/g, '""');
            const needsQuotes =
              escaped.includes(',') ||
              escaped.includes('"') ||
              escaped.includes('\n') ||
              escaped.includes('\r');
            return needsQuotes ? `"${escaped}"` : escaped;
          })
          .join(',')
      )
      .join('\n');

    return csvContent;
  }

  /**
   * Download capture data as CSV file
   */
  static downloadAsCSV(capture: DebugSessionCapture): void {
    const csv = DebugExportService.exportToCSV(capture);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-${capture.candidateName}-${capture.sessionId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  /**
   * Export to TSV for clipboard (optimized for Excel paste)
   */
  static exportToTSV(capture: DebugSessionCapture): string {
    // Similar to CSV but with tabs
    const fieldIds = new Set<string>();
    for (const snapshot of capture.paymentSnapshots) {
      for (const id of Object.keys(snapshot.fields)) {
        fieldIds.add(id);
      }
    }
    const sortedFieldIds = Array.from(fieldIds).sort();

    const headers = ['Payment Date', 'Tax Year', 'Index', 'Interest Only', ...sortedFieldIds];

    const rows = capture.paymentSnapshots.map((snapshot) => [
      snapshot.paymentDate,
      String(snapshot.taxYear),
      String(snapshot.paymentIndex),
      snapshot.isInterestOnly ? 'Yes' : 'No',
      ...sortedFieldIds.map((id) => snapshot.fields[id]?.formatted ?? ''),
    ]);

    // Escape tabs and newlines in cell values for TSV
    return [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            // Replace tabs and newlines with spaces to prevent breaking TSV format
            return String(cell).replace(/[\t\n\r]/g, ' ');
          })
          .join('\t')
      )
      .join('\n');
  }

  /**
   * Copy capture data to clipboard (TSV format)
   */
  static async copyToClipboard(capture: DebugSessionCapture): Promise<void> {
    const tsv = DebugExportService.exportToTSV(capture);
    await navigator.clipboard.writeText(tsv);
  }

  /**
   * Copy summary to clipboard (formatted text)
   */
  static async copySummaryToClipboard(capture: DebugSessionCapture): Promise<void> {
    const { summary } = capture;
    const text = [
      `Debug Capture Summary: ${capture.candidateName}`,
      `Session ID: ${capture.sessionId}`,
      `Captured: ${new Date(capture.timestamp).toLocaleString()}`,
      `Processing Time: ${capture.processingTimeMs.toFixed(2)}ms`,
      '',
      'Summary:',
      `  Total Revenue: $${summary.totalRevenue.toLocaleString()}`,
      `  Total Principal: $${summary.totalPrincipal.toLocaleString()}`,
      `  Total Interest: $${summary.totalInterest.toLocaleString()}`,
      `  Ending DRF: $${summary.endingDRF.toLocaleString()}`,
      `  Payments Processed: ${summary.paymentsProcessed}`,
      `  Active Bonds: ${summary.activeBondCount}`,
      `  Viable: ${summary.isViable ? 'Yes' : 'No'}`,
      summary.failureMessages.length > 0 ? `  Failures: ${summary.failureMessages.join(', ')}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    await navigator.clipboard.writeText(text);
  }

  // ============================================================================
  // Raw Cashflow Export (for LLM analysis)
  // ============================================================================

  /**
   * Export raw cashflow data to JSON
   * Includes full cashflows array, revenueMap, and bond series for complete context
   */
  static exportCashflowsToJSON(
    candidate: Candidate,
    configuration: Configuration,
    pretty = true
  ): string {
    const sortedCashFlows = [...candidate.cashFlows].sort(
      (a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime()
    );

    const exportData: CashflowExportData = {
      $schema: 'bond-calculator-cashflows-v1',
      exportedAt: new Date().toISOString(),
      candidateId: candidate.id,
      candidateName: candidate.name,
      configurationId: configuration.id,

      cashFlows: sortedCashFlows,
      revenueMap: candidate.revenueMap,
      bondSeries: configuration.bondSeries,

      totalPayments: sortedCashFlows.length,
      interestOnlyPayments: sortedCashFlows.filter((cf) => cf.isInterestOnly).length,
      dateRange: {
        first: sortedCashFlows[0]?.paymentDate ?? '',
        last: sortedCashFlows[sortedCashFlows.length - 1]?.paymentDate ?? '',
      },
    };

    return pretty ? JSON.stringify(exportData, null, 2) : JSON.stringify(exportData);
  }

  /**
   * Download raw cashflow data as JSON file
   * File is named for easy identification: candidateName-cashflows-timestamp.json
   */
  static downloadCashflowsAsJSON(candidate: Candidate, configuration: Configuration): void {
    const json = DebugExportService.exportCashflowsToJSON(candidate, configuration);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Create filename with timestamp for uniqueness
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const safeName = candidate.name.replace(/[^a-zA-Z0-9-_]/g, '-');

    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}-cashflows-${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  /**
   * Copy raw cashflows to clipboard as JSON
   * Useful for pasting into a file or sharing directly
   */
  static async copyCashflowsToClipboard(
    candidate: Candidate,
    configuration: Configuration
  ): Promise<void> {
    const json = DebugExportService.exportCashflowsToJSON(candidate, configuration);
    await navigator.clipboard.writeText(json);
  }
}
